/**
 * Utilities for building and parsing cron expressions from UI inputs
 */

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 6 = Saturday

export interface TimeOfDay {
  hour: number; // 0-23
  minute: number; // 0-59
}

export type FrequencyType =
  | 'daily'
  | 'weekdays'
  | 'weekends'
  | 'specific-days';

export type HourlyInterval = 1 | 2 | 4 | 6;

export interface TimeBasedSchedule {
  time: TimeOfDay;
  frequency: FrequencyType;
  specificDays?: DayOfWeek[]; // Only used when frequency is 'specific-days'
  hourlyInterval?: HourlyInterval; // Run every N hours starting at 'time'
}

/**
 * Convert a time-based schedule configuration to a cron expression
 * Cron format: minute hour day month dayOfWeek
 *
 * Examples:
 * - Daily at 9:00 PM: "0 21 * * *"
 * - Weekdays at 8:00 PM: "0 20 * * 1-5"
 * - Monday/Wednesday/Friday at 10:00 PM: "0 22 * * 1,3,5"
 */
export function buildCronExpression(schedule: TimeBasedSchedule): string {
  const { time, frequency, specificDays, hourlyInterval } = schedule;
  const { hour, minute } = time;

  // Basic validation
  if (hour < 0 || hour > 23) {
    throw new Error('Hour must be between 0 and 23');
  }
  if (minute < 0 || minute > 59) {
    throw new Error('Minute must be between 0 and 59');
  }

  // Minute field
  const cronMinute = minute.toString();

  // Hour field - handle hourly intervals
  let cronHour: string;
  if (hourlyInterval && hourlyInterval > 1) {
    // Generate list of hours starting from the specified hour
    // For example: hour=9, interval=2 -> "9,11,13,15,17,19,21,23"
    const hours: number[] = [];
    for (let h = hour; h < 24; h += hourlyInterval) {
      hours.push(h);
    }
    cronHour = hours.join(',');
  } else if (hourlyInterval === 1) {
    // Every hour at the specified minute
    cronHour = '*';
  } else {
    // No interval - run once at the specified time
    cronHour = hour.toString();
  }

  // Day of month (always * for our use case)
  const cronDayOfMonth = '*';

  // Month (always * for our use case)
  const cronMonth = '*';

  // Day of week field
  let cronDayOfWeek: string;

  switch (frequency) {
    case 'daily':
      cronDayOfWeek = '*'; // Every day
      break;

    case 'weekdays':
      cronDayOfWeek = '1-5'; // Monday through Friday (1=Mon, 5=Fri)
      break;

    case 'weekends':
      cronDayOfWeek = '0,6'; // Sunday and Saturday (0=Sun, 6=Sat)
      break;

    case 'specific-days':
      if (!specificDays || specificDays.length === 0) {
        throw new Error('Specific days must be provided when frequency is "specific-days"');
      }
      // Convert to cron day of week (0=Sunday in cron)
      cronDayOfWeek = specificDays.sort((a, b) => a - b).join(',');
      break;

    default:
      throw new Error(`Unknown frequency type: ${frequency}`);
  }

  return `${cronMinute} ${cronHour} ${cronDayOfMonth} ${cronMonth} ${cronDayOfWeek}`;
}

/**
 * Parse a cron expression into a time-based schedule configuration
 * Returns null if the cron expression is too complex to represent in our UI
 */
export function parseCronExpression(cronExpression: string): TimeBasedSchedule | null {
  const parts = cronExpression.trim().split(/\s+/);

  if (parts.length !== 5) {
    return null; // Invalid cron format
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // We support time-based schedules where:
  // - Minute is a single number
  // - Hour is a single number, *, or a comma-separated list (for hourly intervals)
  // - Day of month is * (every day)
  // - Month is * (every month)
  // - Day of week is *, a range, or a list

  if (!/^\d+$/.test(minute)) {
    return null; // Complex minute expressions not supported
  }

  if (dayOfMonth !== '*' || month !== '*') {
    return null; // Day-of-month or month-specific schedules not supported
  }

  const minuteValue = parseInt(minute, 10);

  // Parse hour field - can be single number, *, or comma-separated list
  let hourValue: number;
  let hourlyInterval: HourlyInterval | undefined;

  if (hour === '*') {
    // Every hour at the specified minute
    hourValue = 0; // Use 0 as the base hour for UI display
    hourlyInterval = 1;
  } else if (/^\d+$/.test(hour)) {
    // Single hour - no interval
    hourValue = parseInt(hour, 10);
  } else if (/^[\d,]+$/.test(hour)) {
    // Comma-separated hours - detect interval pattern
    const hours = hour.split(',').map(h => parseInt(h, 10)).sort((a, b) => a - b);

    if (hours.length < 2) {
      return null;
    }

    // Calculate the interval between consecutive hours
    const interval = hours[1] - hours[0];

    // Verify it's a consistent interval and one of our supported values
    const isConsistent = hours.every((h, i) => {
      if (i === 0) return true;
      return h - hours[i - 1] === interval;
    });

    if (isConsistent && (interval === 1 || interval === 2 || interval === 4 || interval === 6)) {
      hourValue = hours[0]; // Use first hour as the base
      hourlyInterval = interval as HourlyInterval;
    } else {
      return null; // Not a supported interval pattern
    }
  } else {
    return null; // Unsupported hour format
  }

  const time: TimeOfDay = {
    hour: hourValue,
    minute: minuteValue,
  };

  // Parse day of week
  let frequency: FrequencyType;
  let specificDays: DayOfWeek[] | undefined;

  if (dayOfWeek === '*') {
    frequency = 'daily';
  } else if (dayOfWeek === '1-5') {
    frequency = 'weekdays';
  } else if (dayOfWeek === '0,6' || dayOfWeek === '6,0') {
    frequency = 'weekends';
  } else {
    // Try to parse as specific days (comma-separated list)
    const dayPattern = /^[\d,]+$/;
    if (dayPattern.test(dayOfWeek)) {
      const days = dayOfWeek.split(',').map(d => parseInt(d, 10) as DayOfWeek);
      if (days.every(d => d >= 0 && d <= 6)) {
        frequency = 'specific-days';
        specificDays = days;
      } else {
        return null;
      }
    } else {
      // Try to parse ranges (e.g., "1-3" for Mon-Wed)
      const rangePattern = /^(\d+)-(\d+)$/;
      const rangeMatch = dayOfWeek.match(rangePattern);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        if (start >= 0 && start <= 6 && end >= 0 && end <= 6 && start <= end) {
          frequency = 'specific-days';
          specificDays = [];
          for (let i = start; i <= end; i++) {
            specificDays.push(i as DayOfWeek);
          }
        } else {
          return null;
        }
      } else {
        return null; // Unsupported day of week pattern
      }
    }
  }

  return {
    time,
    frequency,
    specificDays,
    hourlyInterval
  };
}

/**
 * Format a time for display
 */
export function formatTime(time: TimeOfDay, use24Hour: boolean = false): string {
  const { hour, minute } = time;

  if (use24Hour) {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }

  // 12-hour format with AM/PM
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
}

/**
 * Format frequency for display
 */
export function formatFrequency(frequency: FrequencyType, specificDays?: DayOfWeek[]): string {
  switch (frequency) {
    case 'daily':
      return 'Daily';
    case 'weekdays':
      return 'Weekdays';
    case 'weekends':
      return 'Weekends';
    case 'specific-days':
      if (!specificDays || specificDays.length === 0) {
        return 'Specific days';
      }
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return specificDays.map(d => dayNames[d]).join(', ');
    default:
      return 'Unknown';
  }
}

/**
 * Format a complete time-based schedule for display
 */
export function formatTimeBasedSchedule(schedule: TimeBasedSchedule, use24Hour: boolean = false): string {
  const timeStr = formatTime(schedule.time, use24Hour);
  const freqStr = formatFrequency(schedule.frequency, schedule.specificDays);

  // Handle hourly intervals
  if (schedule.hourlyInterval) {
    const intervalStr = schedule.hourlyInterval === 1
      ? 'Every hour'
      : `Every ${schedule.hourlyInterval} hours`;

    return `${intervalStr} at :${schedule.time.minute.toString().padStart(2, '0')} starting ${timeStr} (${freqStr})`;
  }

  return `${freqStr} at ${timeStr}`;
}
